import { useEffect, useState } from "react";

import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { ModalContent } from "../components/ModalContent";
import { Button } from "@/components/ui/button";

import { Loader2, Navigation, Link2, Hash, Tag } from "lucide-react";

import { useDispatch, useSelector } from "react-redux";

import { showToast } from "@/redux/Toast/actions";
import { callLoader } from "@/redux/Loader/actions";

import ImageUploadField from "@/components/common/ImageUploadField";
import { Input, SelectInput } from "../components/FormControls";
import {
  createBaseNavigation,
  updateBaseNavigation,
} from "@/redux/features/baseNavigation/baseNavigationThunk";

/* ---------------- INITIAL STATE ---------------- */

const initialState = {
  BaseNavigationId: 0,
  AccountTypeId: 0,
  BaseNavigationName: "",
  BaseNavigationURL: "",
  HasNavigation: false,
  IsActive: true,
  SequenceNo: 0,
  CreatedBy: "admin",
  FileData: null,
};

/* ---------------- COMPONENT ---------------- */

export default function AddBaseNavigationModal({
  open,
  onOpenChange,
  onSuccess,
  isEdit = false,
  editData = null,
}) {
  const dispatch = useDispatch();

  const accountTypes = useSelector((state) => state.accountType.accountTypes);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [previewImage, setPreviewImage] = useState(null);

  /* ---------------- PREFILL ON EDIT ---------------- */

  useEffect(() => {
    if (!open) {
      setFormData(initialState);
      setErrors({});
      setPreviewImage(null);
      return;
    }

    if (isEdit && editData) {
      setPreviewImage(editData?.BaseNavigationIcon || null);

      setFormData({
        BaseNavigationId: editData?.BaseNavigationId ?? 0,
        BaseNavigationName: editData?.BaseNavigationName ?? "",
        BaseNavigationURL: editData?.BaseNavigationURL ?? "",
        AccountTypeId: editData?.AccountTypeId ?? 0,
        SequenceNo: editData?.SequenceNo ?? 0,
        HasNavigation: editData?.HasNavigation ?? false,
        IsActive: editData?.IsActive ?? true,
        CreatedBy: editData?.CreatedBy ?? "Admin",
        CreatedOn: editData?.CreatedOn ?? new Date().toISOString(),
        FileData: null,
      });

      return;
    }

    setFormData(initialState);
  }, [open, isEdit, editData]);

  /* ---------------- HANDLERS ---------------- */

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  const validate = () => {
    const err = {};

    if (!formData.BaseNavigationName?.trim()) {
      err.BaseNavigationName = "Navigation name is required";
    }

    if (!formData.BaseNavigationURL?.trim()) {
      err.BaseNavigationURL = "URL is required";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      dispatch(callLoader(true));

      console.log("formData", formData);
      const payload = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        // Don't send empty file field
        if (key === "FileData" && !value) return;

        payload.append(key, value ?? "");
      });

      const thunk = isEdit ? updateBaseNavigation : createBaseNavigation;

      for (const pair of payload.entries()) {
        console.log(pair[0], pair[1]);
      }

      await dispatch(thunk(payload)).unwrap();

      dispatch(
        showToast(
          isEdit
            ? "Navigation updated successfully"
            : "Navigation created successfully",
          "success",
          true,
        ),
      );

      await onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      dispatch(
        showToast(error?.message || "Failed to save navigation", "error", true),
      );
    } finally {
      setLoading(false);
      dispatch(callLoader(false));
    }
  };
  /* ---------------- UI ---------------- */

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ModalContent className="h-[90vh] flex flex-col">
        {/* HEADER */}
        <DialogHeader className="shrink-0 relative border-b border-white/10 px-6 py-5 bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-teal-500/10 flex items-center justify-center">
              <Navigation className="w-5 h-5 text-cyan-500" />
            </div>

            <div>
              <DialogTitle className="text-white">
                {isEdit ? "Update Base Navigation" : "Add Base Navigation"}
              </DialogTitle>

              <DialogDescription className="text-slate-300">
                Manage navigation links and routing configuration.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <Input
            label="Navigation Name"
            name="BaseNavigationName"
            icon={Navigation}
            value={formData.BaseNavigationName}
            onValueChange={handleChange}
            error={errors.BaseNavigationName}
          />

          <Input
            label="Navigation URL"
            name="BaseNavigationURL"
            icon={Link2}
            value={formData.BaseNavigationURL}
            onValueChange={handleChange}
            error={errors.BaseNavigationURL}
          />

          <Input
            label="Sequence No"
            type="number"
            name="SequenceNo"
            icon={Hash}
            value={formData.SequenceNo}
            onValueChange={handleChange}
          />

          <SelectInput
            label="Account Type"
            name="AccountTypeId"
            icon={Tag}
            value={formData.AccountTypeId}
            onValueChange={handleChange}
            options={
              accountTypes?.map((item) => ({
                value: item.VALUE,
                label: item.TEXT,
              })) || []
            }
          />

          {/* TOGGLES */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() =>
                handleChange("HasNavigation", !formData.HasNavigation)
              }
              className={`flex-1 p-3 rounded-lg border ${
                formData.HasNavigation
                  ? "border-emerald-500 bg-emerald-500/10"
                  : ""
              }`}
            >
              Has Navigation
            </button>

            <button
              type="button"
              onClick={() => handleChange("IsActive", !formData.IsActive)}
              className={`flex-1 p-3 rounded-lg border ${
                formData.IsActive ? "border-emerald-500 bg-emerald-500/10" : ""
              }`}
            >
              Active
            </button>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <ImageUploadField
              label="Navigation Logo"
              value={previewImage}
              required={false}
              maxSize={5}
              accept={[
                "image/png",
                "image/jpeg",
                "image/jpg",
                "image/webp",
                "image/svg+xml",
              ]}
              hint="PNG, JPG, WEBP, SVG • Max 5MB"
              onChange={(preview, file) => {
                setPreviewImage(preview);
                handleChange("FileData", file);
              }}
            />
          </div>
        </div>

        {/* FOOTER */}
        <DialogFooter className="border-t border-slate-200 dark:border-slate-800 px-6 py-4 bg-slate-50 dark:bg-slate-900/50">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-teal-500 hover:bg-teal-600 text-white min-w-[140px]"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Navigation className="w-4 h-4 mr-2" />
                {isEdit ? "Update" : "Create"}
              </>
            )}
          </Button>
        </DialogFooter>
      </ModalContent>
    </Dialog>
  );
}
